import { usePlaybackCurrentTs, usePlaybackIsSeeking } from '@app/core/playback/hooks/usePlayback';
import debugFn from 'debug';
import { useEffect, useRef } from 'react';
import { useFocus, useSetFocusCenteredAtTs } from '../../stores/stream-screen.store';

const debug = debugFn('useSyncCursorFromPlayback');

/**
 * Hook to update focus when playhead moves outside focus range during a user-initiated seek.
 * When the video player seeks (via seek bar click), if the playhead ends up outside the
 * current focus range, the focus is updated immediately to center on the playhead
 * while maintaining its current duration.
 */
export function useSyncCursorFromPlayback() {
  const currentTs = usePlaybackCurrentTs();
  const isSeeking = usePlaybackIsSeeking();
  const focus = useFocus();
  const setFocusCenteredAtTs = useSetFocusCenteredAtTs();
  const wasSeekingRef = useRef(false);
  const hasUpdatedForCurrentSeekRef = useRef(false);

  useEffect(() => {
    // Reset update flag when seeking ends
    if (wasSeekingRef.current && !isSeeking) {
      hasUpdatedForCurrentSeekRef.current = false;
    }

    wasSeekingRef.current = isSeeking;

    // Check during seeking if playhead is outside focus range and update
    // This handles both: immediate update when seeking starts (if currentTs is ready)
    // and delayed update when currentTs arrives after isSeeking becomes true
    if (isSeeking && currentTs !== null && !hasUpdatedForCurrentSeekRef.current) {
      const { from: focusFrom, to: focusTo } = focus;

      // Check if playhead is outside the current focus range
      // Only check if focus is initialized (both from and to > 0)
      const isOutsideFocus =
        focusFrom > 0 && focusTo > 0 && (currentTs < focusFrom || currentTs > focusTo);

      if (isOutsideFocus) {
        // Calculate current focus duration
        const focusDuration = focusTo - focusFrom;

        // Shift focus to playhead position while preserving duration
        setFocusCenteredAtTs(currentTs, focusDuration);

        debug('seeking update focus to playhead at %d', currentTs);
        hasUpdatedForCurrentSeekRef.current = true;
      }
    }
  }, [isSeeking, currentTs, focus, setFocusCenteredAtTs]);
}
